"use client";
import "./timeline.scss";
import { useEffect, useRef, useState } from "react";
import { useBlock } from "@/core/blocks/context";
import { Heading, TextAttr, AdvancedImageAttr, RepeaterAttr, SelectAttr, useAttrValue } from "@/core/blocks/attributes";

/**
 * Timeline with three sub-block kinds; a smooth SVG path is drawn through all
 * of the item dots after layout.
 */
export default function TimelineBlock() {
  const ctx = useBlock();
  const wrapRef = useRef<HTMLDivElement>(null);
  const [d, setD] = useState("");

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const compute = () => {
      const rect = wrap.getBoundingClientRect();
      const dots = Array.from(wrap.querySelectorAll<HTMLElement>(".tl-dot"));
      const pts = dots.map((dot) => {
        const r = dot.getBoundingClientRect();
        return { x: r.left - rect.left + r.width / 2, y: r.top - rect.top + r.height / 2 };
      });
      if (pts.length < 2) return setD("");
      let path = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 1; i < pts.length; i++) {
        const prev = pts[i - 1];
        const cur = pts[i];
        const midY = (prev.y + cur.y) / 2;
        path += ` C ${prev.x} ${midY}, ${cur.x} ${midY}, ${cur.x} ${cur.y}`;
      }
      setD(path);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(wrap);
    const t = setTimeout(compute, 300); // after images/fonts settle
    return () => {
      ro.disconnect();
      clearTimeout(t);
    };
  }, [ctx.attrs, ctx.editing]);

  return (
    <section className="block-timeline">
      <div className="container">
        <Heading name="title" className="section-title" placeholder={{ nl: "Tijdlijn", en: "Timeline" }} />
        <div className="tl-wrap" ref={wrapRef}>
          {d && (
            <svg className="tl-path" aria-hidden="true">
              <path d={d} fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="6 6" />
            </svg>
          )}
          <RepeaterAttr
            name="items"
            as="div"
            className="tl-items"
            addLabel={{ nl: "Tijdlijn-item", en: "Timeline item" }}
            itemDefaults={() => ({ kind: "year", year: {}, title: {}, text: {} })}
          >
            {() => <TimelineItem />}
          </RepeaterAttr>
        </div>
      </div>
    </section>
  );
}

function TimelineItem() {
  const ctx = useBlock();
  const kind = useAttrValue<string>("kind", "year");
  return (
    <div className={`tl-item tl-${kind}`}>
      {ctx.editing && (
        <SelectAttr
          name="kind"
          options={[
            { value: "year", label: "Year + text (dot left)" },
            { value: "image", label: "Title + image (dot right)" },
            { value: "split", label: "Text left, image right (dot middle)" },
          ]}
        />
      )}
      {kind === "year" && (
        <>
          <span className="tl-dot" />
          <div className="tl-card">
            <TextAttr name="year" as="span" className="tl-year" placeholder="2024" />
            <TextAttr name="title" as="h3" className="tl-title" placeholder={{ nl: "Titel", en: "Title" }} />
            <TextAttr name="text" rich as="p" placeholder={{ nl: "Beschrijving…", en: "Description…" }} />
          </div>
        </>
      )}
      {kind === "image" && (
        <>
          <div className="tl-card">
            <TextAttr name="title" as="h3" className="tl-title" placeholder={{ nl: "Titel", en: "Title" }} />
            <AdvancedImageAttr name="image" className="tl-img" sizes="(min-width: 900px) 40vw, 100vw" />
          </div>
          <span className="tl-dot" />
        </>
      )}
      {kind === "split" && (
        <>
          <div className="tl-card tl-left">
            <TextAttr name="title" as="h3" className="tl-title" placeholder={{ nl: "Titel", en: "Title" }} />
            <TextAttr name="text" rich as="p" placeholder={{ nl: "Tekst…", en: "Text…" }} />
          </div>
          <span className="tl-dot" />
          <div className="tl-card tl-right">
            <AdvancedImageAttr name="image" className="tl-img" sizes="(min-width: 900px) 40vw, 100vw" />
          </div>
        </>
      )}
    </div>
  );
}

export const meta = { label: "Timeline", icon: "timeline" };

export function defaults(): Record<string, unknown> {
  return {
    title: { nl: "Tijdlijn" },
    items: [{ kind: "year", year: { nl: "2024" }, title: {}, text: {} }],
  };
}
